import { Shield } from 'lucide-react';

export default function PrivacyPolicy() {
  return (
    <div data-testid="privacy-policy-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-meritum-navy via-meritum-teal to-meritum-teal">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl text-center">
          <span className="inline-block px-4 py-2 bg-white/10 border border-white/20 rounded-sm text-meritum-gold font-body text-sm font-medium mb-6">
            Legal
          </span>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight leading-none text-white mb-4">
            Privacy Policy
          </h1>
          <p className="font-body text-base text-slate-200 max-w-2xl mx-auto">
            Your privacy is important to us. Learn how we collect, use, and protect your information.
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <div className="prose prose-slate max-w-none">
            {/* Important Notice */}
            <div className="bg-meritum-teal/10 border-l-4 border-meritum-teal p-6 rounded-r-lg mb-12">
              <div className="flex items-start gap-4">
                <Shield className="text-meritum-teal flex-shrink-0 mt-1" size={24} />
                <div>
                  <h2 className="font-heading text-xl font-semibold text-meritum-navy mt-0 mb-2">
                    Your Data is Protected
                  </h2>
                  <p className="font-body text-slate-600 mb-0">
                    MeritumPraxis collects user information to operate the platform. 
                    User data will not be sold or distributed to third parties without consent.
                  </p>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="space-y-8">
              <div>
                <h2 className="font-heading text-2xl font-semibold text-meritum-navy mb-4">
                  1. Information We Collect
                </h2>
                <p className="font-body text-slate-600 leading-relaxed mb-4">
                  MeritumPraxis collects the following types of information:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 mt-2 bg-meritum-gold rounded-full flex-shrink-0" />
                    <span className="font-body text-slate-600">
                      <strong>Personal Information:</strong> Name, email address, contact details, 
                      educational background, and professional experience.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 mt-2 bg-meritum-gold rounded-full flex-shrink-0" />
                    <span className="font-body text-slate-600">
                      <strong>Achievement Data:</strong> Academic records, certifications, 
                      project documentation, and verified accomplishments.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 mt-2 bg-meritum-gold rounded-full flex-shrink-0" />
                    <span className="font-body text-slate-600">
                      <strong>Usage Data:</strong> Information about how you interact with our platform, 
                      including access times and features used.
                    </span>
                  </li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-semibold text-meritum-navy mb-4">
                  2. How We Use Your Information
                </h2>
                <p className="font-body text-slate-600 leading-relaxed mb-4">
                  We use collected information to:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 mt-2 bg-meritum-teal rounded-full flex-shrink-0" />
                    <span className="font-body text-slate-600">Operate and maintain the MeritumPraxis platform</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 mt-2 bg-meritum-teal rounded-full flex-shrink-0" />
                    <span className="font-body text-slate-600">Facilitate profile creation and verification</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 mt-2 bg-meritum-teal rounded-full flex-shrink-0" />
                    <span className="font-body text-slate-600">Enable skill discovery for organizations</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 mt-2 bg-meritum-teal rounded-full flex-shrink-0" />
                    <span className="font-body text-slate-600">Communicate with users about platform updates</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 mt-2 bg-meritum-teal rounded-full flex-shrink-0" />
                    <span className="font-body text-slate-600">Improve and enhance our services</span>
                  </li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-semibold text-meritum-navy mb-4">
                  3. Data Protection
                </h2>
                <p className="font-body text-slate-600 leading-relaxed">
                  We implement appropriate technical and organizational measures to protect your 
                  personal data against unauthorized access, alteration, disclosure, or destruction. 
                  All data is stored securely and accessed only by authorized personnel for 
                  legitimate business purposes.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-semibold text-meritum-navy mb-4">
                  4. Third-Party Sharing
                </h2>
                <p className="font-body text-slate-600 leading-relaxed">
                  User data will not be sold or distributed to third parties without consent. 
                  Profile information may be visible to organizations using the platform for 
                  skill discovery purposes, as this is the core functionality of MeritumPraxis. 
                  Users have control over their profile visibility settings.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-semibold text-meritum-navy mb-4">
                  5. Your Rights
                </h2>
                <p className="font-body text-slate-600 leading-relaxed mb-4">
                  Under the Protection of Personal Information Act (POPIA) of South Africa, you have the right to:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 mt-2 bg-meritum-gold rounded-full flex-shrink-0" />
                    <span className="font-body text-slate-600">Access your personal information</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 mt-2 bg-meritum-gold rounded-full flex-shrink-0" />
                    <span className="font-body text-slate-600">Request correction of inaccurate information</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 mt-2 bg-meritum-gold rounded-full flex-shrink-0" />
                    <span className="font-body text-slate-600">Request deletion of your information</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 mt-2 bg-meritum-gold rounded-full flex-shrink-0" />
                    <span className="font-body text-slate-600">Object to processing of your information</span>
                  </li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-semibold text-meritum-navy mb-4">
                  6. Contact Us
                </h2>
                <p className="font-body text-slate-600 leading-relaxed">
                  For any privacy-related inquiries or to exercise your rights, please contact us at:{' '}
                  <a href="mailto:info@meritumpraxis.co.za" className="text-meritum-teal hover:underline">
                    info@meritumpraxis.co.za
                  </a>
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-semibold text-meritum-navy mb-4">
                  7. Governing Law
                </h2>
                <p className="font-body text-slate-600 leading-relaxed">
                  This Privacy Policy is governed by the laws of the Republic of South Africa, 
                  including the Protection of Personal Information Act (POPIA). Any disputes 
                  shall be subject to the exclusive jurisdiction of the courts of the Republic of South Africa.
                </p>
              </div>
            </div>

            {/* Last Updated */}
            <div className="mt-12 pt-8 border-t border-slate-200">
              <p className="font-body text-sm text-slate-500">
                <strong>Effective Date:</strong> February 2026<br />
                <strong>Company:</strong> MeritumPraxis (Pty) Ltd<br />
                <strong>Jurisdiction:</strong> Republic of South Africa
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
